import { motion } from 'framer-motion';
import { useTranslation, Trans } from 'react-i18next';
import TestimonialsSection from '../../components/user/TestimonialsSection';
import Register from '../../components/user/Register';

type CoreValue = {
  text: string;
  size: 'large' | 'medium' | 'small';
};

export default function AboutUsPage() {
  const { t } = useTranslation();
  const coreValues = t('about_us.core_values.items', { returnObjects: true }) as CoreValue[];

  document.title = t('about_us.page_title');

  const getSizeClass = (size: 'large' | 'medium' | 'small') => {
    switch (size) {
      case 'large':
        return 'text-4xl md:text-5xl font-bold text-green-700';
      case 'medium':
        return 'text-2xl md:text-3xl font-semibold text-gray-800';
      case 'small':
      default:
        return 'text-lg md:text-xl text-gray-600';
    }
  };

  return (
    <div className="bg-white">
      <section
        className="relative bg-cover bg-center text-white py-28 sm:py-40 h-[50vh]"
        style={{ backgroundImage: `url(/images/background/AboutUs.jpg)` }}
      >
        <div className="absolute inset-0 bg-black bg-opacity-60" />
        <div className="relative max-w-4xl mx-auto px-6 text-center z-10">
          <motion.h1
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            className="text-4xl md:text-6xl font-extrabold tracking-tight"
          >
            {t('about_us.hero.title')}
          </motion.h1>
        </div>
      </section>

      <section className="py-16 sm:py-20 bg-gray-50">
        <div className="max-w-4xl mx-auto px-6">
          <motion.p
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            viewport={{ once: true, amount: 0.5 }}
            className="text-lg md:text-xl text-gray-700 leading-relaxed text-justify"
          >
            <Trans
              i18nKey="about_us.introduction.paragraph"
              components={[<strong />, <strong />]}
            />
          </motion.p>
        </div>
      </section>

      <section className="py-16 sm:py-20 bg-gray-50">
        <div className="max-w-7xl mx-auto px-6 grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-16 items-center">
          <div className="space-y-4 text-center lg:text-left">
            <h2 className="text-3xl font-bold text-gray-900 tracking-tight">{t('about_us.vision.title')}</h2>
            <p className="text-lg text-gray-600 leading-relaxed text-justify">{t('about_us.vision.paragraph')}</p>
          </div>
          <div className="space-y-4 text-center lg:text-left">
            <h2 className="text-3xl font-bold text-gray-900 tracking-tight">{t('about_us.mission.title')}</h2>
            <p className="text-lg text-gray-600 leading-relaxed text-justify">{t('about_us.mission.paragraph')}</p>
          </div>
        </div>
      </section>

      <section className="py-16 sm:py-24 bg-[#DBF8C8]">
        <div className="max-w-7xl mx-auto px-6 text-center">
          <h2 className="text-4xl font-extrabold text-gray-900 tracking-tight">{t('about_us.core_values.title')}</h2>
          <p className="mt-4 max-w-3xl mx-auto text-xl text-gray-600">{t('about_us.core_values.subtitle')}</p>
          
          <div className="mt-16 flex flex-wrap justify-center items-center gap-x-8 md:gap-x-12 gap-y-6">
            {coreValues.map((value, i) => (
              <motion.div
                key={i}
                className={`whitespace-nowrap ${getSizeClass(value.size)}`}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5, delay: i * 0.05 }}
                viewport={{ once: true, amount: 0.5 }}
              >
                {value.text}
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      <TestimonialsSection />
      {/* <Register /> */}
    </div>
  );
}